import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PaymentMethodPicker } from "@/components/PaymentMethodPicker";
import { ImageUploader } from "@/components/ImageUploader";
import { ads, type AdCampaign, type AdPackage } from "@/domains/ads";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";
import { Loader2, Megaphone, Plus, BarChart3 } from "lucide-react";

const statusColor = (s: string) => {
  switch (s) {
    case "active": return "bg-success/15 text-success";
    case "pending_review": return "bg-warning/15 text-warning";
    case "pending_payment": return "bg-muted text-muted-foreground";
    case "rejected": return "bg-destructive/15 text-destructive";
    case "expired": return "bg-muted text-muted-foreground";
    default: return "bg-muted text-muted-foreground";
  }
};

const Ads = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<AdPackage[]>([]);
  const [campaigns, setCampaigns] = useState<AdCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // form
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [ctaLabel, setCtaLabel] = useState("Learn more");
  const [ctaUrl, setCtaUrl] = useState("");
  const [pkgId, setPkgId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // payment
  const [payOpen, setPayOpen] = useState(false);
  const [pendingCampaign, setPendingCampaign] = useState<AdCampaign | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pkgs, mine] = await Promise.all([ads.listPackages(), ads.listMyCampaigns()]);
      setPackages(pkgs);
      setCampaigns(mine);
      if (!pkgId && pkgs[0]) setPkgId(pkgs[0].id);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const selectedPkg = packages.find((p) => p.id === pkgId);

  const handleSubmit = async () => {
    if (!title.trim()) return toast.error("Add a title");
    if (!selectedPkg) return toast.error("Pick a package");
    setSubmitting(true);
    try {
      const c = await ads.createDraft({
        title: title.trim(),
        body: body.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
        cta_label: ctaLabel.trim() || undefined,
        cta_url: ctaUrl.trim() || undefined,
        package_id: selectedPkg.id,
        amount_cents: selectedPkg.price_cents,
      });
      setPendingCampaign(c);
      setPayOpen(true);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onPaymentSuccess = async (provider: "stripe" | "powertranz") => {
    if (!pendingCampaign) return;
    try {
      await ads.markPaid(pendingCampaign.id, provider, `${provider}_${Date.now()}`);
      toast.success("Payment received — campaign is pending admin review");
      setShowForm(false);
      setTitle(""); setBody(""); setImageUrl(""); setCtaUrl("");
      setPendingCampaign(null);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Promote your business</h1>
        <p className="text-center text-xs opacity-80 mt-1">Buy a spot in the Discover feed</p>
      </BrandHeader>

      <div className="px-5 pb-24 space-y-5 mt-4">
        {!showForm && (
          <Button className="w-full rounded-2xl h-12" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-1" /> New ad campaign
          </Button>
        )}

        {showForm && (
          <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" />
              <h2 className="font-display font-bold">New campaign</h2>
            </div>

            <div className="space-y-1">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} placeholder="Catchy headline" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={140} placeholder="One sentence about your offer" rows={2} />
            </div>
            <div className="space-y-1">
              <Label>Image</Label>
              <ImageUploader
                bucket="ad-creatives"
                value={imageUrl}
                onChange={setImageUrl}
                folder="campaigns"
                aspect="video"
                label="Add ad image"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Button label</Label>
                <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} maxLength={20} />
              </div>
              <div className="space-y-1">
                <Label>Link URL</Label>
                <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://…" />
              </div>
            </div>

            <div className="space-y-2 pt-1">
              <Label>Package</Label>
              <div className="grid gap-2">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPkgId(p.id)}
                    className={`text-left rounded-xl border p-3 transition-colors ${
                      pkgId === p.id ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold">{p.name}</span>
                      <span className="font-display font-extrabold text-primary">{formatMoney(p.price_cents)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.description ?? `${p.duration_days} days`}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleSubmit} disabled={submitting || !selectedPkg}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${selectedPkg ? formatMoney(selectedPkg.price_cents) : ""}`}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <h2 className="font-display font-bold text-sm">Your campaigns</h2>
          {loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No campaigns yet.</p>
          ) : (
            campaigns.map((c) => (
              <div key={c.id} className="rounded-2xl bg-card border border-border p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-display font-bold text-sm leading-tight">{c.title}</p>
                  <Badge className={`${statusColor(c.status)} border-0`}>{c.status.replace(/_/g, " ")}</Badge>
                </div>
                {c.body && <p className="text-xs text-muted-foreground line-clamp-2">{c.body}</p>}
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1">
                  <span className="inline-flex items-center gap-1"><BarChart3 className="h-3 w-3" /> {c.impressions} views</span>
                  <span>{c.clicks} clicks</span>
                  <span>{formatMoney(c.amount_cents)}</span>
                  {c.ends_at && c.status === "active" && (
                    <span>ends {new Date(c.ends_at).toLocaleDateString()}</span>
                  )}
                </div>
                {c.status === "rejected" && c.reject_reason && (
                  <p className="text-xs text-destructive mt-1">Rejected: {c.reject_reason}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <PaymentMethodPicker
        open={payOpen}
        onOpenChange={(o) => {
          setPayOpen(o);
          if (!o) load();
        }}
        title="Pay for your ad"
        amountCents={selectedPkg?.price_cents ?? 0}
        currency="USD"
        featureKey={pendingCampaign ? `ad_campaign:${pendingCampaign.id}` : "ad_campaign"}
        onSuccess={onPaymentSuccess}
      />
    </MobileShell>
  );
};

export default Ads;
